/**
 * The page a Google sign-up started from (a contest, a post…): the temporary account is sent to /mon-profil
 * for the finalization form, and comes back here once the form is submitted, instead of the home page.
 * A cookie, like the temporary token, so that a reload in between keeps it. Internal paths only.
 */
export const useSignupReturnTo = () =>
  useCookie<string | undefined>('signupReturnTo', {
    maxAge: 60 * 60 * 24,
    secure: true,
    sameSite: 'lax',
  })

export const isInternalPath = (path?: string | null): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//') && path !== '/mon-profil'

/** the participation form the sign-up started from, to open again once the Waper is back on the contest */
export type SignupReturnModal = 'contest' | 'contestMedia'

export const useSignupReturnModal = () =>
  useCookie<SignupReturnModal | undefined>('signupReturnModal', {
    maxAge: 60 * 60 * 24,
    secure: true,
    sameSite: 'lax',
  })

/**
 * On the page of a contest: opens its participation form again when the Waper comes back from the finalization
 * form of a Google sign-up started in that form. `state` is read once the page is mounted and whenever it
 * changes: 'loading' while the contest is not known yet, 'open' when the Waper can take part, 'skip' otherwise
 * (already took part, contest over). The flag is consumed either way, so that it never opens a form later.
 * The opening waits for the modals closed by the navigation (layout) to be gone.
 */
export const useReopenAfterSignup = (
  modal: SignupReturnModal,
  state: () => 'loading' | 'open' | 'skip',
  open: () => void,
) => {
  const flag = useSignupReturnModal()
  const { authenticated } = useAuthenticated()

  onMounted(() => {
    watch(
      () => (flag.value === modal && authenticated.value ? state() : undefined),
      (current) => {
        if (!current || current === 'loading') return
        flag.value = undefined
        if (current === 'open') setTimeout(open, 150)
      },
      { immediate: true },
    )
  })
}
